import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Zap, Clock, TrendingUp } from "lucide-react";

interface QuickWin {
  title: string;
  description: string;
  timeline: string;
  expectedImpact: string;
}

interface QuickWinsCardProps {
  recommendations: any[];
}

function getQuickWins(recommendations: any[]): QuickWin[] {
  // Extract high priority recommendations that can be done quickly
  const quickWins: QuickWin[] = [];
  
  if (recommendations && recommendations.length > 0) {
    // Filter high priority items first, fallback to first 3
    const highPriority = recommendations.filter(rec => 
      typeof rec === 'object' && rec.priority === 'high'
    ).slice(0, 3);
    
    const items = highPriority.length > 0 ? highPriority : recommendations.slice(0, 3);
    
    items.forEach((rec, idx) => {
      // Handle both object format (from AI) and string format (fallback)
      if (typeof rec === 'object' && rec !== null) {
        quickWins.push({
          title: rec.title || `Działanie ${idx + 1}`,
          description: rec.description || '',
          timeline: rec.timeline || (idx === 0 ? '1-3 dni' : idx === 1 ? '3-7 dni' : '1-2 tyg'),
          expectedImpact: rec.expectedImpact || (idx === 0 ? '+15% zasięg' : idx === 1 ? '+20% engagement' : '+10% konwersje')
        });
      } else if (typeof rec === 'string') {
        // Fallback for string format
        const parts = rec.split('.');
        quickWins.push({
          title: parts[0] || `Działanie ${idx + 1}`,
          description: parts.slice(1).join('.').trim() || rec,
          timeline: idx === 0 ? '1-3 dni' : idx === 1 ? '3-7 dni' : '1-2 tyg',
          expectedImpact: idx === 0 ? '+15% zasięg' : idx === 1 ? '+20% engagement' : '+10% konwersje'
        });
      }
    });
  }
  
  return quickWins;
}

export const QuickWinsCard = ({ recommendations }: QuickWinsCardProps) => {
  const quickWins = getQuickWins(recommendations);
  
  if (quickWins.length === 0) return null;
  
  return (
    <Card className="border-2 border-success shadow-lg relative overflow-hidden">
      <div className="absolute top-0 right-0 -mt-3 -mr-3 z-10">
        <Badge className="bg-gradient-to-r from-success to-success/80 text-success-foreground px-4 py-2 text-sm font-bold shadow-md">
          <Zap className="h-4 w-4 mr-1 inline" />
          Szybkie Wygrane
        </Badge>
      </div>
      <CardHeader className="pb-3">
        <CardTitle className="text-success">🚀 3 rzeczy do zrobienia TERAZ</CardTitle>
        <CardDescription>Minimalny wysiłek, maksymalny efekt (0-7 dni)</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {quickWins.map((win, idx) => (
            <div 
              key={idx} 
              className="flex items-start gap-3 p-4 bg-card rounded-lg border-2 border-success/20 hover:border-success/40 hover:shadow-md transition-all cursor-pointer"
            >
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-success text-success-foreground flex items-center justify-center font-bold">
                {idx + 1}
              </div>
              <div className="flex-1">
                <p className="font-semibold">{win.title}</p>
                <p className="text-sm text-muted-foreground mt-1">{win.description}</p>
                <div className="flex items-center gap-3 mt-2">
                  <Badge variant="outline" className="text-xs">
                    <Clock className="h-3 w-3 mr-1" />
                    {win.timeline}
                  </Badge>
                  <Badge className="bg-success/10 text-success border-success/20 text-xs">
                    <TrendingUp className="h-3 w-3 mr-1" />
                    {win.expectedImpact}
                  </Badge>
                </div>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};
